import "zod/compile";
import { oc } from "@orpc/contract";
import { JSON_SCHEMA_OUTPUT_REGISTRY } from "@orpc/zod/zod4";
import { z } from "zod";
import {
  workbookCatalogEntrySchema,
  workbookDefinitionSchema,
  workbookDiagnosticSchema,
  workbookFileSchema,
  workbookRunSchema,
  workbookRunDetailsSchema,
  workbookTableSchema,
  workbookInspectionSchema,
  workbookResultSchema,
  workbookRunModeSchema,
  workbookSourceSchema,
  workbookResultRowsInputSchema,
  workbookResultRowsSchema,
  workbookAggregateInputSchema,
  workbookReportDataInputSchema,
  workbookReportDataSchema,
  workbookAggregateResultSchema,
  workbookResultSampleInputSchema,
  workbookResultSampleSchema,
  workbookSourceSampleInputSchema,
  workbookSourceSampleSchema,
  workbookProfileRequestSchema,
  workbookProfileSchema,
  workbookSinkDeliverySchema,
  workbookSinkTargetColumnSchema,
  workbookSinkTargetOptionSchema,
  workbookSinkTargetRefSchema,
} from "@erp/shared-types";

// Run modes grow (`only` joined `from`), and listed Runs carry theirs.
// x-extensible-enum tells oasdiff the set is allowed to grow in responses;
// requests keep the closed enum.
JSON_SCHEMA_OUTPUT_REGISTRY.add(workbookRunModeSchema, {
  enum: undefined,
  "x-extensible-enum": workbookRunModeSchema.options,
} as { enum: undefined });
// New source adapters extend the catalog; its response kinds are extensible.
JSON_SCHEMA_OUTPUT_REGISTRY.add(workbookCatalogEntrySchema.shape.kind, {
  enum: undefined,
  "x-extensible-enum": workbookCatalogEntrySchema.shape.kind.options,
} as { enum: undefined });
const id = z.object({ workbookId: z.uuid() });
const summary = z.object({
  id: z.uuid(),
  name: z.string(),
  revision: z.number(),
  updatedAt: z.string(),
  /** Org namespace this Workbook is grouped under (`namespaces.*`), if any. */
  namespaceId: z.uuid().nullable(),
});
const workbook = summary.extend({ definition: workbookDefinitionSchema });
/** A saved Workbook with the Diagnostics of its Definition; they never block the save. */
const savedWorkbook = workbook.extend({
  diagnostics: z.array(workbookDiagnosticSchema),
});
const workbookAutomationSchema = z.object({
  workflowId: z.uuid(),
  workflowName: z.string(),
  isActive: z.boolean(),
  version: z.number().int(),
  relation: z.enum(["runs", "triggered-by"]),
  anyWorkbook: z.boolean(),
});
const workbookTaskSchema = z.object({
  id: z.uuid(),
  /** The human id, such as TASK-42; null before the Task is numbered. */
  displayId: z.string().nullable(),
  title: z.string(),
  status: z.string(),
  createdAt: z.string(),
  workbookRunId: z.uuid().nullable(),
  processStepId: z.string().nullable(),
});
const workbookTaskRuleOutcomeSchema = z.object({
  taskRuleId: z.uuid(),
  opened: z.number().int(),
  existing: z.number().int(),
  truncated: z.boolean(),
  skipped: z.enum(["worksheet-removed", "not-computed"]).nullable(),
});
/** An AI rewrite of a stale Instruction, pending the user's Accept or Discard. */
const sopUpdate = z.object({
  id: z.uuid(),
  stepId: z.string(),
  /** The Revision it was reviewed from. */
  revision: z.number().int(),
  trigger: z.enum(["save", "version", "run", "request"]),
  status: z.enum([
    "matches",
    "proposed",
    "accepted",
    "discarded",
    "unavailable",
  ]),
  /** P(match) the Match check gave; null when the user asked for the rewrite. */
  probability: z.number().min(0).max(1).nullable(),
  baseTitle: z.string().nullable(),
  /** The Instruction's text it rewrote: offered only while the text is unchanged. */
  baseInstructions: z.string(),
  /** Null keeps the current title. */
  proposedTitle: z.string().nullable(),
  proposedInstructions: z.string(),
  summary: z.string().nullable(),
  /** Which check gave `probability`: Jev, or a direct model comparison when Jev was unavailable. */
  matchSource: z.enum(["jev", "model"]).nullable(),
  /** Why Jev gave no answer (`jev not-configured`, `jev timeout`, `jev http-<status>`…), when it gave none. */
  matchDetail: z.string().nullable(),
  /** Why the newest review reached no verdict; retried at the next Run, Save Version or save pause. */
  reason: z.enum(["match-check-unavailable", "rewrite-failed"]).nullable(),
  createdAt: z.string(),
});
export const workbooksContracts = {
  list: oc
    .route({
      summary: "List workbooks",
      description:
        "Returns the organization's workbooks (spreadsheet-style data transformation workbooks) with name, revision, last update and namespace.",
    })
    .input(z.object({}))
    .output(z.array(summary)),
  create: oc
    .route({
      summary: "Create a workbook",
      description:
        "Creates an empty workbook with a name, optionally filed under an organization namespace.",
    })
    .input(
      z.object({
        name: z.string().trim().min(1).max(200),
        /** Org namespace (`namespaces.*`) to file the new Workbook under. */
        namespaceId: z.uuid().nullable().optional(),
      }),
    )
    .output(workbook),
  /**
   * Files the Workbook under an org namespace; null ungroups it. Not part of
   * the Definition, so the Revision is unchanged.
   */
  setNamespace: oc
    .route({
      summary: "Move a workbook to a namespace",
      description:
        "Files a workbook under an organization namespace, or ungroups it with null; the workbook's revision is unchanged.",
    })
    .input(id.extend({ namespaceId: z.uuid().nullable() }))
    .output(summary),
  get: oc
    .route({
      summary: "Get a workbook",
      description:
        "Returns one workbook with its full definition: sources, worksheets, instructions and outputs.",
    })
    .input(id)
    .output(workbook),
  /** Deletes the Workbook with its Artifacts and Runs; refused while a Run is in flight. */
  delete: oc
    .route({
      summary: "Delete a workbook",
      description:
        "Deletes a workbook with its files and run history; refused while a run is in progress.",
    })
    .input(id)
    .output(z.object({ success: z.literal(true) })),
  save: oc
    .route({
      summary: "Save a workbook definition",
      description:
        "Saves an edited workbook definition if the expected revision still matches, optionally as a restorable checkpoint; returns the saved workbook with diagnostics.",
    })
    .input(
      id.extend({
        expectedRevision: z.number().int().nonnegative(),
        definition: workbookDefinitionSchema,
        checkpoint: z.boolean().default(false),
      }),
    )
    .output(savedWorkbook),
  versions: oc
    .route({
      summary: "List saved versions of a workbook",
      description:
        "Returns a workbook's checkpointed versions with revision number and save time.",
    })
    .input(id)
    .output(
      z.array(
        z.object({ id: z.uuid(), revision: z.number(), createdAt: z.string() }),
      ),
    ),
  restore: oc
    .route({
      summary: "Restore a workbook version",
      description:
        "Replaces the workbook's definition with an earlier saved version, if the expected revision still matches; returns the workbook with diagnostics.",
    })
    .input(
      id.extend({
        versionId: z.uuid(),
        expectedRevision: z.number().int().nonnegative(),
      }),
    )
    .output(savedWorkbook),
  upload: oc
    .route({
      summary: "Upload a file to a workbook",
      description:
        "Attaches an Excel .xlsx file, sent as base64 (about 5 MB max), to a workbook for use as a source.",
    })
    .input(
      id.extend({
        fileName: z.string().min(1).max(200),
        base64: z.string().max(7_000_000),
      }),
    )
    .output(workbookFileSchema),
  inspect: oc
    .route({
      summary: "Inspect an uploaded workbook file",
      description:
        "Reads an attached file's sheets and rows from a chosen sheet and start row, to set up a source from it.",
    })
    .input(
      id.extend({
        fileId: z.uuid(),
        sheetName: z.string().optional(),
        startRow: z.number().int().min(1).max(100000).default(1),
      }),
    )
    .output(workbookInspectionSchema),
  /**
   * A Source's leading rows, reading only them, and its row count: exact, or
   * for a Large file not yet counted an estimate (`rowCountEstimated`) until
   * `sourceRowCount` reads the true one.
   */
  source: oc
    .route({
      summary: "Preview a workbook source's rows",
      description:
        "Reads a workbook source's leading rows and its row count, exact or estimated for large files.",
    })
    .input(id.extend({ source: workbookSourceSchema }))
    .output(
      workbookTableSchema.safeExtend({
        rowCount: z.number().int().nonnegative(),
        rowCountEstimated: z.boolean().optional(),
      }),
    ),
  /**
   * A Sample of a Source before it is added (Add data's preview): first, last,
   * seeded random, stratified or most recent rows of its leading rows.
   */
  sourceSample: oc
    .route({
      summary: "Sample a workbook source's rows",
      description:
        "Returns a sample of a source's rows (first, last, random, stratified or most recent), taken from a bounded number of its leading rows, with row positions.",
    })
    .input(workbookSourceSampleInputSchema)
    .output(workbookSourceSampleSchema),
  /**
   * A Data profile of Sources (as they would be read, at most `maxRows` rows
   * each) and Run Results: column statistics and key matches both ways,
   * never rows. Each Source needs the Read permission reading it does.
   */
  profile: oc
    .route({
      summary: "Profile workbook data columns",
      description:
        "Returns column statistics and key matches across workbook sources and run results, without rows; reads at most a set number of source rows.",
    })
    .input(id.extend({ request: workbookProfileRequestSchema }))
    .output(workbookProfileSchema),
  /** A Source's true row count, counted where its rows are; cached per data version. */
  sourceRowCount: oc
    .route({
      summary: "Count rows in a workbook source",
      description:
        "Counts a workbook source's exact row count where the data lives; cached per data version.",
    })
    .input(id.extend({ source: workbookSourceSchema }))
    .output(z.object({ rowCount: z.number().int().nonnegative() })),
  catalog: oc
    .route({
      summary: "List data available to workbooks",
      description:
        "Returns the data sources, data mappings, uploaded data assets and business entities a workbook can read from, with their columns and versions.",
    })
    .input(z.object({}))
    .output(z.array(workbookCatalogEntrySchema)),
  schemaGroupMembers: oc
    .route({
      summary: "List the data sources a group of a schema reads",
      description:
        "Returns every readable data source of one schema definition, uncapped, so a group can be built from its complete membership.",
    })
    .input(z.object({ schemaId: z.uuid() }))
    .output(z.array(z.object({ id: z.uuid(), name: z.string() }))),
  groupableSchemas: oc
    .route({
      summary: "List the schemas whose data sources can be combined",
      description:
        "Returns every schema definition with two or more readable data sources, uncapped, with how many it has and each one's name, so no per-schema request is needed.",
    })
    .input(z.object({}))
    .output(
      z.array(
        z.object({
          id: z.uuid(),
          name: z.string(),
          linkCount: z.number().int().positive(),
          members: z.array(z.object({ id: z.uuid(), name: z.string() })),
        }),
      ),
    ),
  run: oc
    .route({
      summary: "Run a workbook",
      description:
        "Starts a run of the saved workbook: all worksheets, up to or from a target worksheet or output, or a sample; requires the expected revision.",
    })
    .input(
      id.extend({
        expectedRevision: z.number().int().nonnegative(),
        mode: workbookRunModeSchema.default("entire"),
        /**
         * The Target: a Worksheet for up-to and from, or an Output for up-to
         * (its Worksheets). Required for both; ignored for entire.
         */
        targetId: z.uuid().nullable().default(null),
      }),
    )
    .output(workbookRunSchema),
  /**
   * Run from this sheet for the edited, unsaved Definition, on the Base run's
   * Snapshot. Returns the target's and downstream Results; persists nothing.
   */
  preview: oc
    .route({
      summary: "Preview an unsaved workbook worksheet",
      description:
        "Computes a worksheet and its downstream results for an edited, unsaved definition using the last run's data snapshot; saves nothing.",
    })
    .input(
      id.extend({
        definition: workbookDefinitionSchema,
        targetSheetId: z.uuid(),
        /**
         * Who asks: the editor's Live preview, or the agent trying an edit.
         * Each replaces only its own queued preview, so neither cancels the other's.
         */
        requester: z.enum(["editor", "agent"]).default("editor"),
      }),
    )
    .output(
      z.object({
        baseRunId: z.uuid(),
        /** When the Base run's inputs were read, for the Sources it reused. */
        inputsAsOf: z.string(),
        /** The Sources read fresh because the Base run's Snapshot lacks a column now used, and when. */
        freshSources: z.array(
          z.object({ id: z.uuid(), name: z.string(), readAt: z.string() }),
        ),
        /** How many Sources the preview reads that it reused from the Base run's Snapshot. */
        reusedSourceCount: z.number().int().nonnegative(),
        results: z.array(workbookResultSchema),
      }),
    ),
  /**
   * A first look at a Worksheet no Run holds the inputs of yet: the edited,
   * unsaved target and its upstream computed over the first `headRows` rows
   * of each Source it reads. Persists nothing.
   */
  firstLook: oc
    .route({
      summary:
        "Preview an unsaved workbook worksheet over its sources' first rows",
      description:
        "Computes a worksheet and the worksheets it reads for an edited, unsaved definition over only the first rows of each source, for a worksheet no earlier run can be reused for; saves nothing.",
    })
    .input(
      id.extend({
        definition: workbookDefinitionSchema,
        targetSheetId: z.uuid(),
        /** Who asks; each replaces only its own queued preview. */
        requester: z.enum(["editor", "agent"]).default("editor"),
      }),
    )
    .output(
      z.object({
        /** The Sources read, and when. */
        freshSources: z.array(
          z.object({ id: z.uuid(), name: z.string(), readAt: z.string() }),
        ),
        /** How many of each Source's first rows it read. */
        headRows: z.number().int().positive(),
        results: z.array(workbookResultSchema),
      }),
    ),
  /**
   * P(the Instruction's Linked items already implement it) for the edited Definition,
   * or null when the AI check is unavailable, failed or timed out. Asked before
   * regenerating an already-applied Instruction; it never blocks Generation.
   */
  instructionMatch: oc
    .route({
      summary: "Check if an instruction is already implemented",
      description:
        "Uses AI to estimate the probability that an instruction's linked worksheet steps already implement it in the edited definition; null when unavailable.",
    })
    .input(
      id.extend({
        definition: workbookDefinitionSchema,
        stepId: z.string().min(1).max(200),
      }),
    )
    .output(z.object({ probability: z.number().min(0).max(1).nullable() })),
  /** The pending SOP update of each Instruction. */
  sopUpdates: oc
    .route({
      summary: "List pending SOP updates for a workbook",
      description:
        "Returns the pending standard-operating-procedure text update proposed for each workbook instruction.",
    })
    .input(id)
    .output(z.array(sopUpdate)),
  /** Queues an SOP review of the saved Revision, after the editor's autosaves pause. */
  reviewSop: oc
    .route({
      summary: "Queue an SOP review of a workbook",
      description:
        "Queues a review of the saved workbook's instructions that proposes SOP text updates, when any instruction is a candidate; returns whether one was queued.",
    })
    .input(id)
    .output(z.object({ queued: z.boolean() })),
  /** Regenerates these Instructions from their configuration in the edited Definition, as proposals. */
  requestSopUpdates: oc
    .route({
      summary: "Regenerate SOP text for workbook instructions",
      description:
        "Uses AI to regenerate up to 50 instructions' SOP text from their configuration in the edited definition, returned as proposals to accept or discard.",
    })
    .input(
      id.extend({
        definition: workbookDefinitionSchema,
        stepIds: z.array(z.string().min(1).max(200)).min(1).max(50),
      }),
    )
    .output(z.array(sopUpdate)),
  /** Accept or Discard a proposal (the editor applies an accepted rewrite), or reopen it on undo. */
  resolveSopUpdate: oc
    .route({
      summary: "Accept or discard an SOP update",
      description:
        "Accepts or discards a proposed SOP text update for a workbook instruction, or reopens it on undo.",
    })
    .input(
      id.extend({
        updateId: z.uuid(),
        resolution: z.enum(["accept", "discard", "reopen"]),
      }),
    )
    .output(
      z.object({
        id: z.uuid(),
        status: z.enum([
          "matches",
          "proposed",
          "accepted",
          "discarded",
          "unavailable",
        ]),
      }),
    ),
  /** The newest Runs, light: without each Result's Input-order preview and Distinct values. */
  runs: oc
    .route({
      summary: "List recent workbook runs",
      description:
        "Returns a workbook's newest runs with status, mode and result summaries, omitting row previews and distinct values.",
    })
    .input(id)
    .output(z.array(workbookRunSchema)),
  /** Stops a queued or running editor Run; a finished Run is returned unchanged. */
  cancelRun: oc
    .route({
      summary: "Cancel a workbook run",
      description:
        "Cancels a queued or running run started from the editor and returns it; a finished run is returned unchanged. A run started by a workflow or an agent is refused: cancel the workflow that started it.",
    })
    .input(id.extend({ runId: z.uuid() }))
    .output(workbookRunSchema),
  /** What `runs` leaves out of one Run's Results; a Run's Results never change. */
  runDetails: oc
    .route({
      summary: "Get full results of a workbook run",
      description:
        "Returns the row previews and distinct values per result that the run list omits for one workbook run.",
    })
    .input(id.extend({ runId: z.uuid() }))
    .output(workbookRunDetailsSchema),
  /**
   * A File Output's download: each tab its Worksheet's newest Current Result in
   * the saved Definition. CSV holds a File of exactly one tab.
   */
  download: oc
    .route({
      summary: "Download a workbook file output",
      description:
        "Returns a download link for a file output as Excel or CSV, built from each tab's newest result; refused if validation failed unless accepted.",
    })
    .input(
      id.extend({
        outputId: z.uuid(),
        format: z.enum(["xlsx", "csv"]).default("xlsx"),
        /**
         * Download even though a tab's Result failed an error-severity
         * Validation; without it, such a download is refused, naming the failure.
         */
        acceptFailedValidation: z.boolean().default(false),
      }),
    )
    .output(z.object({ url: z.string() })),
  /** The tables a Data sink can write: Data Source Link ingest tables and Stateful tables. */
  sinkTargets: oc
    .route({
      summary: "List tables a workbook can write to",
      description:
        "Returns the data-source ingest tables and stateful tables a workbook data-sink output can write into.",
    })
    .input(z.object({}))
    .output(z.array(workbookSinkTargetOptionSchema)),
  /**
   * The columns of a sink target a mapping can write, with the table's upsert
   * key flagged (key columns must be mapped). NOT_FOUND when the target isn't
   * the organization's or has no table.
   */
  sinkTargetColumns: oc
    .route({
      summary: "List columns of a workbook sink table",
      description:
        "Returns the writable columns of a data-sink target table, flagging the upsert key columns that must be mapped.",
    })
    .input(workbookSinkTargetRefSchema)
    .output(z.array(workbookSinkTargetColumnSchema)),
  /**
   * Delivers a Data sink Output of the saved Definition now: upserts its
   * Worksheet's newest Current Result into the target table. Problems come back
   * as `issues` (nothing written unless only "rows-failed").
   */
  deliverSink: oc
    .route({
      summary: "Write a workbook output to its table",
      description:
        "Upserts a data-sink output's newest worksheet result into its target table now; returns issues, and writes nothing unless only some rows failed.",
    })
    .input(id.extend({ outputId: z.uuid() }))
    .output(workbookSinkDeliverySchema),
  /**
   * A Sample of a Result (a Large one's grid view): first, last, seeded random,
   * stratified or most recent rows, over every row of the Result.
   */
  resultSample: oc
    .route({
      summary: "Sample rows from a workbook result",
      description:
        "Returns a sample of a run result's rows: first, last, random, stratified or most recent, with row positions and total row count.",
    })
    .input(workbookResultSampleInputSchema)
    .output(workbookResultSampleSchema),
  /** An attached File's bytes as uploaded: a short-lived link for its Preview and Download. */
  fileDownload: oc
    .route({
      summary: "Download a file attached to a workbook",
      description:
        "Returns a short-lived link to an attached workbook file as uploaded, for preview or download.",
    })
    .input(id.extend({ fileId: z.uuid() }))
    .output(z.object({ url: z.string(), fileName: z.string() })),
  /** A Result's rows for a Report: all within the Client row limit, else none (use `aggregate`). */
  resultRows: oc
    .route({
      summary: "Get workbook result rows for a report",
      description:
        "Returns all rows of a run result for a report when within the client row limit, otherwise none.",
    })
    .input(workbookResultRowsInputSchema)
    .output(workbookResultRowsSchema),
  /**
   * A Report's reads at once — each component's Server aggregation or leading
   * rows — so the server reads and aggregates each Result once per Report.
   */
  reportData: oc
    .route({
      summary: "Get data for a workbook report",
      description:
        "Returns every component's aggregation or leading rows for a workbook report in one call, reading each result once.",
    })
    .input(workbookReportDataInputSchema)
    .output(workbookReportDataSchema),
  /** Server aggregation of a Result, for a Report past the Client row limit. */
  aggregate: oc
    .route({
      summary: "Aggregate a workbook result",
      description:
        "Runs a server-side grouping and aggregation over a run result, for reports past the client row limit.",
    })
    .input(workbookAggregateInputSchema)
    .output(workbookAggregateResultSchema),
  /**
   * Emails a Report Output of the saved Definition, as the browser rendered it
   * (a PDF attached, a PNG inline), to the requesting user only.
   */
  sendReportTestEmail: oc
    .route({
      summary: "Email yourself a workbook report test",
      description:
        "Emails a rendered report output (PDF attached, PNG inline) to the requesting user only, as a test of report delivery.",
    })
    .input(
      id.extend({
        outputId: z.uuid(),
        pdfBase64: z.string().min(1).max(8_000_000),
        pngBase64: z.string().min(1).max(6_000_000),
      }),
    )
    .output(z.object({ sentTo: z.string() })),
  /**
   * The Automations tab: workflows that run the Workbook (a Run Workbook node)
   * or react to its Runs (a Workbook Run Completed trigger), and the Agent
   * definitions it is attached to as a tool.
   */
  automations: oc
    .route({
      summary: "List automations using a workbook",
      description:
        "Returns workflows that run the workbook or trigger on its run completion, and agents that use it as a tool.",
    })
    .input(id)
    .output(
      z.object({
        workflows: z.array(workbookAutomationSchema),
        agents: z.array(
          z.object({ agentDefinitionId: z.uuid(), name: z.string() }),
        ),
      }),
    ),
  /** Tasks the Workbook opened, by its Task rules or its Process steps, newest first. */
  tasks: oc
    .route({
      summary: "List tasks opened by a workbook",
      description:
        "Returns tasks a workbook opened through its task rules or human-in-the-loop process steps, newest first.",
    })
    .input(id)
    .output(z.array(workbookTaskSchema)),
  /** Applies the Task rules to a completed Run by hand; rows that have their Task already get none. */
  openRunTasks: oc
    .route({
      summary: "Open tasks for a workbook run",
      description:
        "Applies the workbook's task rules to a completed run by hand, creating tasks for rows that don't already have one; returns outcomes.",
    })
    .input(id.extend({ runId: z.uuid() }))
    .output(z.array(workbookTaskRuleOutcomeSchema)),
  /** A human-in-the-loop Process step: a Task for one saved Instruction, linked back to the Workbook. Idempotent per `requestId`. */
  createStepTask: oc
    .route({
      summary: "Create a task for a workbook step",
      description:
        "Opens a human-in-the-loop task for one saved workbook instruction with optional assignee, priority and due date; idempotent per request ID.",
    })
    .input(
      id.extend({
        stepId: z.string().min(1).max(200),
        assigneeUserId: z.uuid().nullable().optional(),
        priority: z.enum(["low", "medium", "high", "urgent"]).optional(),
        dueDate: z.iso.datetime().nullable().optional(),
        /** The caller's id for this request: resubmitting it opens no second Task. */
        requestId: z.uuid(),
      }),
    )
    .output(z.object({ taskId: z.uuid(), displayId: z.string().nullable() })),
};
export type WorkbookAutomation = z.infer<typeof workbookAutomationSchema>;
export type WorkbookTask = z.infer<typeof workbookTaskSchema>;
export type WorkbookTaskRuleOutcome = z.infer<
  typeof workbookTaskRuleOutcomeSchema
>;
export type WorkbookDetail = z.infer<typeof workbook>;
export type WorkbookSopUpdate = z.infer<typeof sopUpdate>;
export type WorkbookInspection = z.infer<typeof workbookInspectionSchema>;
